import React, { useEffect, useState } from "react";
import { Spinner } from "react-bootstrap";
import { FaTimesCircle } from "react-icons/fa";
import { useSelector } from "react-redux";

import "./ContactUsForm.css";
import { submitContactUsForm } from "@/api/contactUs";
import { validateForm } from "@/utils/formValidator";
import { Link, useLocation } from "react-router";
import { formatPhoneNumber } from "@/utils/phoneNumberFormatter";
import { trackApiCall, trackButtonClick } from "@/utils/analytics";

const ContactUsForm = () => {
  const userData = useSelector((state) => state?.user);
  const location = useLocation();

  const [isSuccesfullSubmitted, setSuccesfullSubmitted] = useState(false);
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    phoneNumber: "",
    email: "",
    question: "",
    propertyURL: "",
  });

  const [formErrors, setFormErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [messageType, setMessageType] = useState(""); // 'success' or 'error'

  const handleClear = () => {
    // clear the form after success
    setFormData({
      firstName: "",
      lastName: "",
      phoneNumber: "",
      email: "",
      question: "",
      propertyURL: "",
    });
    setTimeout(() => {
      setMessage("");
      setFormErrors({});
    }, 4000);
  };

  const submitFormApi = async () => {
    setLoading(true);
    setMessage("");
    const digitsOnly = formData?.phoneNumber?.replace(/[^\d]/g, "");

    trackButtonClick({
      eventName: "contact_us_submit_click",
      eventCategory: "Contact Us form",
      eventSource: "Contact Us Submit Tapped",
    });

    try {
      const data = {
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email,
        phone: digitsOnly,
        questions: formData.question,
        propertyAddress: formData.propertyURL,
      };

      const response = await submitContactUsForm(data);
      if (response?.status === "FAILURE") {
        setMessage(
          response?.message || "Unable to Save the lead. Please try Again."
        );
        setMessageType("error");
        return;
      }
      setSuccesfullSubmitted(true);
      setMessageType("success");
      handleClear();

      trackApiCall({
        eventName: "contact_us_submit_success",
        eventCategory: "Contact Us form",
        eventSource: "Contact Us Submit Success",
      });
    } catch (error) {
      setMessage("There was an error submitting the form. Please try again.");
      setMessageType("error");
      setSuccesfullSubmitted(false);
    } finally {
      setLoading(false);
      // Hide the message after 4 seconds
      setTimeout(() => setMessage(""), 4000);
    }
  };

  const handleChange = (event) => {
    const { name, value } = event.target;

    // Update form data
    if (name === "phoneNumber") {
      // Remove any non-digit characters before checking length
      const digitsOnly = value.replace(/[^\d]/g, "");
      if (digitsOnly.length <= 10) {
        const formattedNumber = formatPhoneNumber(digitsOnly);
        setFormData((prevData) => ({ ...prevData, [name]: formattedNumber }));
      } else return;
    } else {
      setFormData((prevData) => ({ ...prevData, [name]: value }));
    }

    // Clear error for the field being changed
    if (formErrors[name]) {
      setFormErrors((prevErrors) => ({
        ...prevErrors,
        [name]: "",
      }));
    }
  };

  const handleBlur = (event) => {
    const { name, value } = event.target;

    // For phone number, strip non-digit characters before validation
    if (name === "phoneNumber") {
      const digitsOnly = value.replace(/[^\d]/g, "");
      const validationErrors = validateForm({ [name]: digitsOnly }, [name]);
      setFormErrors((prevErrors) => ({
        ...prevErrors,
        [name]: validationErrors[name] || "",
      }));
    } else {
      const validationErrors = validateForm({ [name]: value }, [name]);
      setFormErrors((prevErrors) => ({
        ...prevErrors,
        [name]: validationErrors[name] || "",
      }));
    }
  };

  const submitForm = async (e) => {
    e.preventDefault();

    const formDataForValidation = {
      ...formData,
      phoneNumber: formData.phoneNumber.replace(/[^\d]/g, ""),
    };

    const fieldsToValidate = Object.keys(formData).filter((field) => {
      if (field === "propertyURL") {
        return formData[field].trim() !== "";
      }
      return true;
    });

    const newErrors = validateForm(formDataForValidation, fieldsToValidate);
    setFormErrors(newErrors);

    if (Object.keys(newErrors).length === 0) {
      submitFormApi();
    } else {
      console.log("Form submission failed due to validation errors.");
    }
  };

  useEffect(() => {
    const propertyUrl = location.state?.propertyUrl;
    const cleanUrl = propertyUrl ? propertyUrl.includes("/property/") && propertyUrl.replace("/property/", "") ||
      propertyUrl.includes("/bank-owned-home/") && propertyUrl.replace("/bank-owned-home/", "") ||
      propertyUrl.includes("/short-sale-home/") && propertyUrl.replace("/short-sale-home/", "") ||
      propertyUrl.includes("/non-bank-owned-home/") && propertyUrl.replace("/non-bank-owned-home/", "") ||
      propertyUrl.includes("/foreclosure-home/") && propertyUrl.replace("/foreclosure-home/", "") ||
      propertyUrl.includes("/second-chance-home/") && propertyUrl.replace("/second-chance-home/", "") ||
      propertyUrl.includes("/absolute-auction-home/") && propertyUrl.replace("/absolute-auction-home/", "") ||
      propertyUrl.includes("/non-performing-loan/") && propertyUrl.replace("/non-performing-loan/", "") ||
      propertyUrl.includes("/commercial-building/") && propertyUrl.replace("/commercial-building/", "") ||
      propertyUrl.includes("/bank-owned-home/preview/") && propertyUrl.replace("/bank-owned-home/preview/", "") ||
      propertyUrl.includes("/short-sale-home/preview/") && propertyUrl.replace("/short-sale-home/preview/", "") ||
      propertyUrl.includes("/non-bank-owned-home/preview/") && propertyUrl.replace("/non-bank-owned-home/preview/", "") ||
      propertyUrl.includes("/foreclosure-home/preview/") && propertyUrl.replace("/foreclosure-home/preview/", "") ||
      propertyUrl.includes("/second-chance-home/preview/") && propertyUrl.replace("/second-chance-home/preview/", "") ||
      propertyUrl.includes("/absolute-auction-home/preview/") && propertyUrl.replace("/absolute-auction-home/preview/", "") ||
      propertyUrl.includes("/non-performing-loan/preview/") && propertyUrl.replace("/non-performing-loan/preview/", "") ||
      propertyUrl.includes("/commercial-building/preview/") && propertyUrl.replace("/commercial-building/preview/", "")
    : "";

    if (userData?.isUserLoggedIn && userData?.user) {
      setFormData((prevData) => ({
        ...prevData,
        firstName: userData?.user?.userFirstName || "",
        lastName: userData?.user?.userLastName || "",
        email: userData?.user?.userEmailId || "",
        phoneNumber: formatPhoneNumber(userData?.user?.primaryPhone) || "",
        propertyURL: cleanUrl,
      }));
    } else if (propertyUrl) {
      setFormData((prevData) => ({
        ...prevData,
        propertyURL: cleanUrl,
      }));
    }
  }, [userData, location.state]);

  const renderSuccesfully = () => (
    <div className="text-center contact_us_success_container">
      <div className="contact_us_success">
        <div className="contact_us_success_tick"></div>
        <div className="success-title contact_us_success_title">Thank You.</div>
        <div className="success-desc">
          <div className="success-desc-text contact_us_success_desc">
            Our customer care representative will reach out to you shortly.
          </div>
        </div>
      </div>
    </div>
  );

  const renderForm = () => (
    <>
      {/* Success/Error Message */}
      {message && messageType === "error" && (
        <div
          className={`message ${messageType} contact-us-com-modal-container`}
        >
          <div className="contact-us-com-modal-message">
            <span className="message-icon me-2 mt-1">
              <FaTimesCircle size={20} color="red" />
            </span>
            {message}
          </div>
        </div>
      )}
      <div className="mb-5 contact_right">
        <div className="row">
          {/* First Name */}
          <div className="col-12 mb-4">
            <input
              type="text"
              name="firstName"
              className={`form-control-custom-class form-control ${
                formErrors.firstName ? "custom-is-invalid" : ""
              }`}
              id="firstName"
              aria-label="firstName"
              placeholder="First Name*"
              value={formData.firstName || ""}
              onChange={handleChange}
              onBlur={handleBlur}
            />
            {formErrors.firstName && (
              <p className="custom-error-message">{formErrors.firstName}</p>
            )}
          </div>

          {/* Last Name */}
          <div className="col-12 mb-4">
            <input
              type="text"
              name="lastName"
              className={`form-control-custom-class form-control ${
                formErrors.lastName ? "custom-is-invalid" : ""
              }`}
              id="lastName"
              aria-label="lastName"
              placeholder="Last Name*"
              value={formData.lastName || ""}
              onChange={handleChange}
              onBlur={handleBlur}
            />
            {formErrors.lastName && (
              <p className="custom-error-message">{formErrors.lastName}</p>
            )}
          </div>

          {/* Email */}
          <div className="col-12 mb-4">
            <input
              type="email"
              name="email"
              className={`form-control-custom-class form-control ${
                formErrors.email ? "custom-is-invalid" : ""
              }`}
              id="email"
              aria-label="email"
              placeholder="Email*"
              value={formData.email || ""}
              onChange={handleChange}
              onBlur={handleBlur}
            />
            {formErrors.email && (
              <p className="custom-error-message">{formErrors.email}</p>
            )}
          </div>

          {/* Phone Number */}
          <div className="col-12 mb-4">
            <input
              type="text"
              name="phoneNumber"
              className={`form-control-custom-class form-control ${
                formErrors.phoneNumber ? "custom-is-invalid" : ""
              }`}
              id="phoneNumber"
              aria-label="phoneNumber"
              placeholder="Phone*"
              value={formData.phoneNumber || ""}
              onChange={handleChange}
              onBlur={handleBlur}
            />
            {formErrors.phoneNumber && (
              <p className="custom-error-message">{formErrors.phoneNumber}</p>
            )}
          </div>

          {/* Property URL */}
          <div className="col-12 mb-4">
            <input
              type="text"
              name="propertyURL"
              className={`form-control-custom-class form-control ${
                formErrors.propertyURL ? "custom-is-invalid" : ""
              }`}
              id="propertyURL"
              aria-label="propertyURL"
              placeholder="Property Address"
              value={formData.propertyURL || ""}
              onChange={handleChange}
              // onBlur={handleBlur}
            />
            {formErrors.propertyURL && (
              <p className="custom-error-message">{formErrors.propertyURL}</p>
            )}
          </div>

          {/* Question */}
          <div className="col-12 mb-4">
            <input
              type="text"
              name="question"
              className={`form-control-custom-class form-control ${
                formErrors.question ? "custom-is-invalid" : ""
              }`}
              id="question"
              aria-label="question"
              placeholder="Enter your question here*"
              value={formData.question || ""}
              onChange={handleChange}
              onBlur={handleBlur}
            />
            {formErrors.question && (
              <p className="custom-error-message">{formErrors.question}</p>
            )}
          </div>

          {/* Submit Button */}
          <div className="col-12 mb-4">
            <button
              type="submit"
              aria-label="submit"
              className="w-100 contact_us_button_wrapper"
              onClick={submitForm}
            >
              {loading ? (
                <Spinner
                  animation="border"
                  size="sm"
                  role="status"
                  aria-hidden="true"
                />
              ) : (
                "Submit"
              )}
              {loading && " Submitting..."}
            </button>
          </div>
        </div>
        {/* Terms & Conditions Disclaimer */}
        <div className="contact_us_disclaimer text-center">
          <p>
            I agree to be contacted according to these{" "}
            <Link to="/about/terms">
              <span className="nowrap contact-us-terms-link">
                Terms & Conditions
              </span>
            </Link>
            . This consent includes receiving autodialed calls and texts from
            Altisource Online Auction, Inc. and its affiliates. I understand
            that this consent is not a condition to receiving any services.
          </p>
        </div>
      </div>
    </>
  );

  return isSuccesfullSubmitted ? renderSuccesfully() : renderForm();
};

export default ContactUsForm;
